/**
 * The one file in the PiP stack that knows LiveKit exists (#1307; promoted
 * from rokhop #1217).
 *
 * It implements `RoomHandle` — connect, publish the mic, tell me when the
 * agent is audible / talking / gone, stream me transcription segments, carry
 * one typed turn out — over `livekit-client`. Everything above it
 * (`session.ts`, the widget, both brand screens) is written against that
 * interface, which is why the state machine’s tests need no WebRTC.
 *
 * `livekit-client` is loaded by **dynamic import inside `connect()`**, not at
 * module scope: most visits never start a voice session and the SDK has no
 * business in the first-paint bundle. The import happens after the Owner has
 * already granted the microphone, inside a wait the widget is narrating.
 *
 * No `@livekit/components-react` — the machine consumes raw room events so
 * both brands share one path (LMC’s legacy page keeps useTranscriptions()
 * until it is retired; the PiP does not depend on it).
 *
 * #1307 additions: `sendText` (lk.chat topic â the duplex carrier the agent’s
 * custom text_input_cb consumes), `onTranscription` (lk.transcription text
 * streams; speaker attribution = attrs['lk.transcribed_track_id'] vs our mic
 * publication sid, LMC’s proven approach), `setMicEnabled` (mute control).
 */
import type { RoomHandle, TranscriptSegment } from './types'

// LiveKit’s reserved text-stream topics (agents SDK types.py: TOPIC_CHAT /
// the transcription publisher). Named here, used nowhere else in the core.
const TOPIC_CHAT = 'lk.chat'
const TOPIC_TRANSCRIPTION = 'lk.transcription'
const ATTR_TRANSCRIBED_TRACK = 'lk.transcribed_track_id'
// #1332: LiveKit publishes each transcribed segment as TWO OR MORE streams —
// one or more interim, then a final — and `reader.info.id` identifies the
// STREAM, not the utterance. The stable per-utterance identity is
// `lk.segment_id`, which interim and final share; `lk.transcription_final`
// says which is which. Keying on the stream id renders every interim
// hypothesis as its own bubble.
const ATTR_SEGMENT_ID = 'lk.segment_id'
const ATTR_TRANSCRIPTION_FINAL = 'lk.transcription_final'

/** The subset of `livekit-client`'s Room we touch. Keeps `any` out of the file. */
interface LkParticipant {
  isLocal: boolean
  audioLevel: number
}
interface LkTrack {
  kind: string
  attach(): HTMLMediaElement
  detach(): HTMLMediaElement[]
}
interface LkTextStreamReader {
  info: { id: string; attributes?: Record<string, string> }
  [Symbol.asyncIterator](): AsyncIterator<string>
}
interface LkRoom {
  on(event: string, cb: (...args: never[]) => void): unknown
  connect(url: string, token: string): Promise<void>
  disconnect(): Promise<void>
  startAudio(): Promise<void>
  registerTextStreamHandler(
    topic: string,
    cb: (reader: LkTextStreamReader, participant: { identity: string }) => void | Promise<void>,
  ): void
  localParticipant: {
    isLocal: boolean
    audioLevel: number
    setMicrophoneEnabled(enabled: boolean): Promise<{ trackSid?: string } | undefined>
    sendText(text: string, options?: { topic?: string }): Promise<unknown>
  }
}

export function createLiveKitRoom(): RoomHandle {
  let room: LkRoom | null = null
  let attached: HTMLMediaElement[] = []
  let tracks: LkTrack[] = []
  let agentSpeaking = false
  let lastRemote: LkParticipant | null = null
  let localMicSid: string | null = null

  let onPresent: (() => void) | null = null
  let onSpeaking: ((speaking: boolean) => void) | null = null
  let onClosed: (() => void) | null = null
  let onSegment: ((segment: TranscriptSegment) => void) | null = null
  let announcedPresent = false

  function announcePresent(): void {
    if (announcedPresent) return
    announcedPresent = true
    onPresent?.()
  }

  return {
    onAgentPresent(cb) {
      onPresent = cb
    },
    onAgentSpeaking(cb) {
      onSpeaking = cb
    },
    onClosed(cb) {
      onClosed = cb
    },
    onTranscription(cb) {
      onSegment = cb
    },

    async connect(url, token) {
      const lk = await import('livekit-client')
      const instance = new lk.Room({
        // The Owner is one person on one mic; the agent is one voice. Nothing
        // here needs simulcast, adaptive stream or dynacast.
        adaptiveStream: false,
        dynacast: false,
      }) as unknown as LkRoom
      room = instance

      instance.on(lk.RoomEvent.TrackSubscribed, ((track: LkTrack) => {
        if (track.kind !== lk.Track.Kind.Audio) return
        // Attaching is what makes the agent audible. Kept off-screen: the
        // widget is the voice surface, an <audio> control is not part of it.
        const el = track.attach()
        el.style.display = 'none'
        tracks.push(track)
        attached.push(el)
        document.body.appendChild(el)
        announcePresent()
      }) as (...args: never[]) => void)

      instance.on(lk.RoomEvent.ActiveSpeakersChanged, ((speakers: LkParticipant[]) => {
        const remote = (speakers || []).find((p) => p && !p.isLocal) ?? null
        lastRemote = remote ?? lastRemote
        const speaking = !!remote
        if (speaking === agentSpeaking) return
        agentSpeaking = speaking
        onSpeaking?.(speaking)
      }) as (...args: never[]) => void)

      instance.on(lk.RoomEvent.Disconnected, (() => {
        onClosed?.()
      }) as (...args: never[]) => void)

      // Live transcript: the agent publishes BOTH speakers' transcriptions on
      // lk.transcription. A stream is NOT an utterance — one utterance is
      // carried by an interim stream (revised as the speaker talks) and then
      // a separate final stream, both stamped with the same `lk.segment_id`.
      // Emit the accumulating text under that segment id so consumers replace
      // in place; see ATTR_SEGMENT_ID above.
      instance.registerTextStreamHandler(TOPIC_TRANSCRIPTION, async (reader) => {
        const emit = onSegment
        if (!emit) return
        const attrs = reader.info.attributes ?? {}
        const speaker: TranscriptSegment['speaker'] =
          localMicSid && attrs[ATTR_TRANSCRIBED_TRACK] === localMicSid ? 'user' : 'agent'
        // Absent `lk.segment_id` we are talking to a publisher that sends one
        // stream per utterance, so the stream id IS the utterance id and its
        // close is the only "final" signal available — the pre-#1332 behaviour.
        const segmentId = attrs[ATTR_SEGMENT_ID]
        const id = segmentId || reader.info.id
        const finalAttr = attrs[ATTR_TRANSCRIPTION_FINAL]
        const closesAsFinal = finalAttr === undefined ? true : finalAttr === 'true'
        let text = ''
        try {
          for await (const chunk of reader) {
            text += chunk
            onSegment?.({ id, text, final: false, speaker })
          }
        } catch {
          /* a dropped stream ends the utterance with what we have */
        }
        if (text) onSegment?.({ id, text, final: closesAsFinal, speaker })
      })

      await instance.connect(url, token)

      // Autoplay: `start()` was called from the Owner’s click, so this is
      // still inside a gesture-blessed window on the engines that require
      // one. Failure here is not fatal — LiveKit retries on the next
      // interaction — so it must not take the conversation down.
      try {
        await instance.startAudio()
      } catch {
        /* the browser will let audio through on the next gesture */
      }
    },

    async publishMic() {
      if (!room) throw new Error('publishMic before connect')
      const pub = await room.localParticipant.setMicrophoneEnabled(true)
      // Speaker attribution for lk.transcription reads against this sid.
      localMicSid = pub?.trackSid ?? localMicSid
    },

    async setMicEnabled(enabled) {
      if (!room) throw new Error('setMicEnabled before connect')
      const pub = await room.localParticipant.setMicrophoneEnabled(enabled)
      if (enabled) localMicSid = pub?.trackSid ?? localMicSid
    },

    async sendText(text) {
      if (!room) throw new Error('sendText before connect')
      await room.localParticipant.sendText(text, { topic: TOPIC_CHAT })
    },

    level() {
      if (!room) return 0
      const source = agentSpeaking ? lastRemote : room.localParticipant
      const value = source?.audioLevel ?? 0
      return Number.isFinite(value) ? value : 0
    },

    async close() {
      const closing = room
      room = null
      onPresent = null
      onSpeaking = null
      onSegment = null
      // `disconnect()` fires Disconnected; the machine already knows it is
      // tearing down, so dropping the callback first avoids a second trip.
      onClosed = null
      for (const track of tracks) {
        try {
          track.detach()
        } catch {
          /* already gone */
        }
      }
      for (const el of attached) el.remove()
      tracks = []
      attached = []
      announcedPresent = false
      agentSpeaking = false
      lastRemote = null
      localMicSid = null
      if (closing) await closing.disconnect()
    },
  }
}
